import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Dropdown,
  Table,
  Badge,
  Alert,
} from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/light.css";
import { getInventorySalesReport } from "api/apis";

interface Filters {
  searchTerm: string;
  dateRange: Date[] | null;
  timePeriod: string;
  status: string; // For summary view (all, in_stock, few_left, sold_out)
  orderStatus: string; // For details view (all statuses)
  sku: string;
  productId: string;
  viewType: "summary" | "details";
}

interface InventorySalesReportParams {
  search?: string;
  startDate?: string;
  endDate?: string;
  period?: string;
  stockStatus?: string;
  orderStatus?: string;
  status?: string;
  sku?: string;
  searchTerm?: string;
  productId?: string;
  export?: boolean;
  view?: "current" | "all";
  currentView?: "summary" | "details";
}

interface InventoryReportItem {
  productId: string;
  productName: string;
  productImages: string[];
  sku: string;
  variantId: string;
  currentStock: number;
  updatedAt: string;
  totalSold: number;
  uniqueCustomers: number;
  totalRevenue: number;
  averageOrderValue: number;
  createdAt: Date;
  status: string;
  salesDetails: Array<{
    orderId: string;
    date: string;
    customer: {
      id: string;
      name: string;
      email: string;
    };
    shippingAddress: {
      address: string;
      city: string;
      country: string;
      zipCode: string;
    };
    quantity: number;
    revenue: number;
    status: string;
    paymentMethod: string;
  }>;
}

interface SalesDetail {
  productId: string;
  productName: string;
  sku: string;
  orderId: string;
  variantId: string;
  date: string;
  customer: {
    id: string;
    name: string;
    email: string;
  };
  shippingAddress: {
    address: string;
    city: string;
    country: string;
    zipCode: string;
  };
  quantity: number;
  revenue: number;
  status: string;
  paymentMethod: string;
}

interface ReportData {
  success: boolean;
  period: string;
  summary: {
    totalProducts: number;
    totalSkus: number;
    totalInStock: number;
    totalOutOfStock: number;
    totalNotInInventory: number;
    totalItemsSold: number;
    totalRevenue: number;
    totalUniqueCustomers: number;
    period: string;
  };
  inventoryReport: InventoryReportItem[];
  salesDetails: SalesDetail[];
  count: number;
}

const Reports = () => {
  const [filters, setFilters] = useState<Filters>({
    searchTerm: "",
    dateRange: null,
    timePeriod: "all",
    status: "all", // Default to all stock statuses
    orderStatus: "", // Default to all order statuses
    sku: "",
    productId: "",
    viewType: "summary", // Default view
  });

  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"summary" | "details">("summary");
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    new Set()
  );

  const handleFilterChange = useCallback((name: keyof Filters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const toggleProductExpand = useCallback((productId: string) => {
    setExpandedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  }, []);

  const buildApiParams = useCallback(
    (includeExport = false): InventorySalesReportParams => {
      const params: InventorySalesReportParams = {};

      if (filters.searchTerm) {
        params.searchTerm = filters.searchTerm; // Send as searchTerm
        params.sku = filters.searchTerm; // Also send as sku for backward compatibility
      }
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.startDate = filters.dateRange[0].toISOString();
        params.endDate = filters.dateRange[1].toISOString();
      }

      if (filters.timePeriod && filters.timePeriod !== "all") {
        const getPeriodMapping = (timePeriod: string): string => {
          const periodMap = {
            today: "daily",
            yesterday: "daily",
            last7: "weekly",
            last30: "monthly",
            month: "monthly",
            lastMonth: "monthly",
            quarter: "quarterly",
            year: "yearly",
            custom: "custom",
          };

          return periodMap[timePeriod as keyof typeof periodMap] || timePeriod;
        };

        params.period = getPeriodMapping(filters.timePeriod);
      }

      // Replace the current status logic with:
      if (filters.viewType === "summary" && filters.status !== "all") {
        params.status = filters.status; // Use status parameter
      } else if (filters.viewType === "details" && filters.orderStatus) {
        params.orderStatus = filters.orderStatus; // Use orderStatus parameter
      }

      if (filters.sku) params.sku = filters.sku;
      if (filters.productId) params.productId = filters.productId;
      if (includeExport) params.export = true;

      return params;
    },
    [filters]
  );

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = buildApiParams();
      console.log("Fetching report with params:", params); // Debug log
      const data = await getInventorySalesReport(params);
      console.log("Filtered data : ", data);
      setReportData(data);
    } catch (error: any) {
      console.error("Error fetching report data:", error);

      // More specific error handling
      if (error.response?.status === 404) {
        setError(
          "API endpoint not found. Please check if the backend route '/api/reports/inventory-sales' is configured correctly."
        );
      } else if (error.response?.status === 500) {
        setError("Server error occurred. Please try again later.");
      } else if (error.code === "NETWORK_ERROR") {
        setError("Network error. Please check your connection.");
      } else {
        setError(
          `Failed to fetch report data: ${error.message || "Unknown error"}`
        );
      }
    } finally {
      setLoading(false);
    }
  }, [buildApiParams]);

  useEffect(() => {
    fetchReportData();
  }, []);

  const handleExport = useCallback(
    async (type: "csv" | "xlsx", view: "current" | "all") => {
      try {
        setError(null);
        const params = buildApiParams(true);

        // Add view parameter to the API call
        params.view = view;
        params.currentView = activeTab; // Send the current active tab

        console.log("Exporting report with params:", params);
        const result = await getInventorySalesReport(params);

        // Create download link
        const url = window.URL.createObjectURL(result.data);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          result.filename ||
          `inventory-report-${view === "current" ? activeTab : "all"}-${
            filters.timePeriod || "all"
          }.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error: any) {
        console.error("Error exporting report:", error);
        setError(
          `Failed to export report: ${error.message || "Unknown error"}`
        );
      }
    },
    [buildApiParams, filters.timePeriod, activeTab]
  );

  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  const getStatusBadge = useCallback((status: string) => {
    const statusMap = {
      "In Stock": "success",
      "Out of Stock": "danger",
      "Not in Inventory": "secondary",
    };
    const variant = statusMap[status as keyof typeof statusMap] || "secondary";
    return <Badge bg={variant}>{status}</Badge>;
  }, []);

  const getOrderStatusBadge = useCallback((status: string) => {
    const statusMap = {
      Processing: "info",
      "Not processed": "warning",
      Shipped: "primary",
      Delivered: "success",
      Cancelled: "danger",
    };
    const variant = statusMap[status as keyof typeof statusMap] || "secondary";
    return <Badge bg={variant}>{status}</Badge>;
  }, []);

  const renderSummaryCards = () => (
    <Row className="mb-4">
      <Col md={3}>
        <Card className="card-animate">
          <Card.Body>
            <div className="d-flex justify-content-between">
              <div>
                <p className="fw-medium text-muted mb-0">Products</p>
                <h2 className="mt-4 ff-secondary fw-semibold">
                  {reportData?.summary.totalProducts || 0}
                </h2>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="card-animate">
          <Card.Body>
            <div className="d-flex justify-content-between">
              <div>
                <p className="fw-medium text-muted mb-0">Variants</p>
                <h2 className="mt-4 ff-secondary fw-semibold">
                  {reportData?.summary.totalSkus || 0}
                </h2>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="card-animate">
          <Card.Body>
            <div className="d-flex justify-content-between">
              <div>
                <p className="fw-medium text-muted mb-0">Items Sold</p>
                <h2 className="mt-4 ff-secondary fw-semibold">
                  {reportData?.summary.totalItemsSold || 0}
                </h2>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="card-animate">
          <Card.Body>
            <div className="d-flex justify-content-between">
              <div>
                <p className="fw-medium text-muted mb-0">Revenue</p>
                <h2 className="mt-4 ff-secondary fw-semibold">
                  {formatCurrency(reportData?.summary.totalRevenue || 0)}
                </h2>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderProductRow = (item: InventoryReportItem) => (
    <tr key={`${item.productId}-${item.sku}`}>
      <td>
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0 me-2">
            {item.productImages.length > 0 && (
              <img
                src={item.productImages[0]}
                alt={item.productName}
                className="avatar-xs rounded"
              />
            )}
          </div>
          <div className="flex-grow-1">
            <h6 className="mb-0">{item.productName}</h6>
            <small className="text-muted">{item.variantId}</small>
          </div>
        </div>
      </td>
      <td>{item.sku}</td>
      <td>
        {new Date(item.createdAt).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })}
      </td>
      <td className="text-end">{item.currentStock}</td>
      <td className="text-end">{item.totalSold}</td>
      <td className="text-end">{formatCurrency(item.totalRevenue)}</td>
      <td>{getStatusBadge(item.status)}</td>
    </tr>
  );

  const renderSalesDetailRow = (sale: SalesDetail) => (
    <tr key={`${sale.orderId}-${sale.sku}`}>
      <td>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <h6 className="mb-0">{sale.productName}</h6>
            <small className="text-muted">{sale.variantId}</small>
          </div>
        </div>
      </td>
      <td>{sale.sku}</td>
      <td>{sale.orderId}</td>
      <td>{formatDate(sale.date)}</td>
      <td>
        {sale.customer ? (
          <>
            {sale.customer.name}
            <br />
            <small>{sale.customer.email}</small>
          </>
        ) : (
          "Guest"
        )}
      </td>
      <td className="text-end">{sale.quantity}</td>
      <td className="text-end">{formatCurrency(sale.revenue)}</td>
      <td>{getOrderStatusBadge(sale.status)}</td>
      <td>{sale.paymentMethod}</td>
    </tr>
  );

  const handleTabChange = (tab: "summary" | "details") => {
    setActiveTab(tab);
    handleFilterChange("viewType", tab);
  };

  const resetFilters = useCallback(() => {
    setFilters({
      searchTerm: "",
      dateRange: null,
      timePeriod: "all",
      status: "all",
      orderStatus: "",
      sku: "",
      productId: "",
      viewType: "summary",
    });
    setActiveTab("summary");
  }, []);

  return (
    <div className="page-content">
      <Card>
        <Card.Header className="border-bottom-dashed">
          <Row className="g-3">
            {/* Search Box */}
            <Col xxl={3} lg={6}>
              <div className="search-box">
                <input
                  type="text"
                  className="form-control search"
                  placeholder="Search for SKU"
                  value={filters.searchTerm}
                  onChange={(e) =>
                    handleFilterChange("searchTerm", e.target.value)
                  }
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            </Col>

            {/* Date Range Picker */}
            <Col xxl={2} lg={6}>
              <Flatpickr
                className="form-control flatpickr-input"
                placeholder="Select Date"
                options={{
                  mode: "range",
                  dateFormat: "d M, Y",
                }}
                value={filters.dateRange || undefined}
                onChange={(dates) => {
                  handleFilterChange("dateRange", dates);
                  if (dates.length > 0) {
                    handleFilterChange("timePeriod", "custom");
                  }
                }}
              />
            </Col>

            {/* Status Filter - Conditionally rendered based on view */}
            {activeTab === "summary" ? (
              <Col xxl={2} lg={6}>
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="all">All Stock</option>
                  <option value="in_stock">In Stock</option>
                  <option value="few_left">Few Left</option>
                  <option value="sold_out">Sold Out</option>
                </select>
              </Col>
            ) : (
              <Col xxl={2} lg={6}>
                <select
                  className="form-select"
                  value={filters.orderStatus}
                  onChange={(e) =>
                    handleFilterChange("orderStatus", e.target.value)
                  }
                >
                  <option value="">All Statuses</option>
                  <option value="Processing">Processing</option>
                  <option value="Not processed">Not processed</option>
                  <option value="Shipped">Shipped</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </Col>
            )}

            {/* Time Period Filter */}
            <Col xxl={2} lg={6}>
              <select
                className="form-select"
                value={filters.timePeriod}
                onChange={(e) => {
                  handleFilterChange("timePeriod", e.target.value);
                  if (e.target.value !== "custom") {
                    handleFilterChange("dateRange", null);
                  }
                }}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="month">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="quarter">Quarterly</option>
                <option value="year">Yearly</option>
                <option value="custom">Custom Range</option>
              </select>
            </Col>

            {/* Filter Button */}
            <Col xxl={3} lg={6} className="d-flex gap-2">
              <Button
                variant="primary"
                type="button"
                className="w-100"
                onClick={fetchReportData}
                disabled={loading}
              >
                {loading ? "Loading..." : "Apply"}
              </Button>
              <Button
                variant="light"
                type="button"
                className="w-100"
                onClick={resetFilters}
                disabled={loading}
              >
                Reset
              </Button>
            </Col>

            {/* Export Dropdown */}
            <Col xxl={2} lg={6}>
              <Dropdown>
                <Dropdown.Toggle variant="soft-secondary" className="w-100">
                  <i className="ri-download-2-line align-bottom me-1"></i>
                  Export
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleExport("csv", "current")}>
                    <i className="ri-file-text-line align-bottom me-1"></i>
                    Export Current View (CSV)
                  </Dropdown.Item>
                  {/* <Dropdown.Item
                    onClick={() => handleExport("xlsx", "current")}
                  >
                    <i className="ri-file-excel-line align-bottom me-1"></i>
                    Export Current View (Excel)
                  </Dropdown.Item> */}
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleExport("csv", "all")}>
                    <i className="ri-file-text-line align-bottom me-1"></i>
                    Export All (CSV)
                  </Dropdown.Item>
                  {/* <Dropdown.Item onClick={() => handleExport("xlsx", "all")}>
                    <i className="ri-file-excel-line align-bottom me-1"></i>
                    Export All (Excel)
                  </Dropdown.Item> */}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading && (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {!loading && reportData && (
            <>
              <div className="mb-4">
                <Button
                  variant={activeTab === "summary" ? "primary" : "light"}
                  className="me-2"
                  onClick={() => handleTabChange("summary")}
                >
                  Summary View
                </Button>
                <Button
                  variant={activeTab === "details" ? "primary" : "light"}
                  onClick={() => handleTabChange("details")}
                >
                  Detailed Transactions
                </Button>
              </div>

              {renderSummaryCards()}

              {/* Add this section to render the appropriate table */}
              <div className="table-responsive">
                <Table className="table-borderless align-middle mb-0">
                  <thead className="table-light">
                    {activeTab === "summary" ? (
                      <tr>
                        <th scope="col">Product</th>
                        <th scope="col">SKU</th>
                        <th scope="col">Date</th>
                        <th scope="col" className="text-end">
                          Stock
                        </th>
                        <th scope="col" className="text-end">
                          Sold
                        </th>
                        <th scope="col" className="text-end">
                          Revenue
                        </th>
                        <th scope="col">Status</th>
                        {/* <th scope="col">Actions</th> */}
                      </tr>
                    ) : (
                      <tr>
                        <th scope="col">Product</th>
                        <th scope="col">SKU</th>
                        <th scope="col">Order ID</th>
                        <th scope="col">Date</th>
                        <th scope="col">Customer</th>
                        <th scope="col" className="text-end">
                          Qty
                        </th>
                        <th scope="col" className="text-end">
                          Amount
                        </th>
                        <th scope="col">Status</th>
                        <th scope="col">Payment</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {activeTab === "summary"
                      ? reportData.inventoryReport.map(renderProductRow)
                      : reportData.salesDetails.map(renderSalesDetailRow)}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {!loading && !reportData && (
            <div className="text-center py-4">
              <h5>No data available</h5>
              <p className="text-muted">
                No inventory sales data found for the selected criteria
              </p>
              <Button variant="primary" onClick={fetchReportData}>
                Load Data
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Reports;
